
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  BankingAPI,
  IBankSlipReceipt
} from '@/modules/transaction/services/banking';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { getString } from '@/modules/common/helpers/resources';
import { dateViewer } from '@/modules/common/helpers/date';
import { formatCurrency } from '@/modules/common/helpers/currency';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import {
  RoundedIcon,
  BaseText,
  Heading,
  Modal,
  ModalButton,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { Notification } from '@/modules/common/components/Notification';

const TIMEOUT_FOR_RELOAD = 3000;

@Component({
  components: {
    RoundedIcon,
    LoaderIndicator,
    Modal,
    Notification,
    BaseText,
    Heading,
    ModalButton
  }
})
export default class PaymentReceipt extends Vue {
  @Prop({ type: String, required: true })
  private id!: string;

  private isLoading = true;
  private isOpenModal = false;
  private receipt?: IBankSlipReceipt;
  private colors = NebraskaColors;

  private async beforeMount() {
    await this.loadReceipt();
  }

  @Watch('id')
  private async loadReceipt() {
    try {
      if (this.id) {
        const bankingAPI = new BankingAPI();
        this.receipt = await bankingAPI.getBankSlipReceipt(this.id);
        this.isLoading = !this.receipt;
      } else {
        this.receipt = undefined;
      }
    } catch (e) {
      this.receipt = undefined;
    }
  }

  private async downloadReceipt() {
    try {
      if (this.id) {
        const bankingAPI = new BankingAPI();
        const file = await bankingAPI.getBankSlipReceiptPdf(this.id, 'pdf');

        const fileURL = window.URL.createObjectURL(new Blob([file]));

        const link = document.createElement('a');
        link.href = fileURL;
        link.setAttribute('download', 'document.pdf');
        document.body.appendChild(link);
        link.click();
      }
    } catch (err) {
      this.$notify({
        group: 'wrn-payment-schedule-notifications',
        text: err.message,
        type: NotificationTypes.error
      });
    }
  }

  private reloadPage() {
    setTimeout(() => {
      window.location.reload();
    }, TIMEOUT_FOR_RELOAD);
  }

  private async cancelPaymentScheduled() {
    try {
      if (this.id) {
        const bankingAPI = new BankingAPI();

        await bankingAPI.cancelPaymentScheduled(this.id);
        this.$notify({
          group: 'wrn-payment-schedule-notifications',
          text: this.getResource('schedule_canceled_successfully'),
          type: NotificationTypes.success
        });

        this.reloadPage();
        this.isOpenModal = false;
      }
    } catch (err) {
      this.$notify({
        group: 'wrn-payment-schedule-notifications',
        text: err.message,
        type: NotificationTypes.error
      });
    }
  }

  private openModal() {
    this.isOpenModal = true;
  }

  private closeModal() {
    this.isOpenModal = false;
  }

  private getStatus(receipt: IBankSlipReceipt) {
    let status = receipt.status;
    if (!status) return '';

    if (receipt.scheduled && receipt.status === 'pending') {
      status = 'scheduled';
    }

    const statusKey = [
      'pending',
      'processing',
      'scheduled',
      'accepted',
      'canceled'
    ].includes(status)
      ? status
      : 'pending';
    const key = `transactions_payment_receipt_status_${statusKey}`;

    return this.getResource(key);
  }

  private getResource(key: string) {
    return getString(key);
  }

  private formatCurrency(value: number) {
    return formatCurrency(value);
  }

  private formatDate(date: string, format?: string) {
    return dateViewer(date, format);
  }
}
